import type { CreateTodo, Todo, UpdateTodo } from "@ronin/contracts";
import { toast } from "@ronin/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api.js";
import { showError } from "../../lib/errors.js";

export const todosQuery = queryOptions({ queryKey: ["todos"], queryFn: () => api.todos.list() });

/** Todo data and mutations. The server returns the list already sorted (spec01 FR7). */
export function useTodos() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: todosQuery.queryKey });

  const query = useQuery(todosQuery);
  const create = useMutation({
    mutationFn: (input: CreateTodo) => api.todos.create(input),
    onSuccess: refresh,
    onError: showError("the todo"),
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateTodo }) => api.todos.update(id, patch),
    onSuccess: refresh,
    onError: showError("the todo"),
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.todos.restore(id),
    onSuccess: refresh,
    onError: showError("the todo", "restored"),
  });
  const remove = useMutation({
    mutationFn: (todo: Todo) => api.todos.remove(todo.id),
    onSuccess: (_result, todo) => {
      void refresh();
      toast("Todo deleted", {
        description: todo.title,
        duration: 8000,
        action: { label: "Undo", onClick: () => restore.mutate(todo.id) },
      });
    },
    onError: showError("the todo", "deleted"),
  });

  return { query, create, update, remove };
}
